import { motion } from "framer-motion";
import { GraduationCap, Clock, CheckCircle2, PlayCircle, Download, ExternalLink, Linkedin, Leaf, Pin, Sparkles, Search } from "lucide-react";
import { API } from "../../lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SOURCE_META, LEVEL_CLS, fmtCpd } from "./useAcademy";

function SourceBadge({ source }) {
  const m = SOURCE_META[source] || SOURCE_META.linkedin_link;
  const Icon = source === "sustek" ? Leaf : Linkedin;
  return (
    <span data-testid="course-source-badge" className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border", m.cls)}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

function SustekActions({ c, actions }) {
  if (c.status === "completed") return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-sm text-emerald-600 flex items-center gap-1.5 font-medium"><CheckCircle2 className="w-4 h-4" /> Completed</span>
      <a href={`${API}/academy/courses/${c.id}/certificate`} target="_blank" rel="noreferrer" data-testid={`certificate-${c.id}`}>
        <Button size="sm" variant="outline" className="h-8"><Download className="w-4 h-4 mr-1.5" /> Certificate</Button>
      </a>
    </div>
  );
  if (c.status === "in_progress") return (
    <Button size="sm" onClick={() => actions.complete.mutate(c.id)} disabled={actions.complete.isPending} data-testid={`complete-course-${c.id}`} className="w-full bg-emerald-600 hover:bg-emerald-700">Mark complete (+{c.cpd_credits} CPD)</Button>
  );
  return (
    <Button size="sm" variant="outline" onClick={() => actions.enroll.mutate(c.id)} disabled={actions.enroll.isPending} data-testid={`enroll-course-${c.id}`} className="w-full"><PlayCircle className="w-4 h-4 mr-1.5" /> Start course</Button>
  );
}

function LinkedInActions({ c, actions }) {
  if (c.link_type === "search") return (
    <a href={c.url} target="_blank" rel="noreferrer" data-testid={`explore-li-${c.id}`} className="block">
      <Button size="sm" variant="outline" className="w-full"><Search className="w-4 h-4 mr-1.5" /> Explore on LinkedIn Learning</Button>
    </a>
  );
  return (
    <div className="flex flex-col gap-2">
      <a href={c.url} target="_blank" rel="noreferrer" data-testid={`open-li-${c.id}`} className="block" onClick={() => actions.openLi.mutate(c.id)}>
        <Button size="sm" className="w-full bg-[#0A66C2] hover:bg-[#004182] text-white"><ExternalLink className="w-4 h-4 mr-1.5" /> Open in LinkedIn Learning</Button>
      </a>
      {c.status === "completed" ? (
        <span data-testid={`li-completed-${c.id}`} className="text-xs text-emerald-700 flex items-center justify-center gap-1.5 font-medium bg-emerald-50 rounded-lg py-1.5">
          <CheckCircle2 className="w-3.5 h-3.5" /> Completed · self-declared · +{fmtCpd(c.cpd_credits)} CPD
        </span>
      ) : (
        <Button size="sm" variant="outline" onClick={() => actions.completeLi.mutate(c.id)} disabled={actions.completeLi.isPending} data-testid={`complete-li-${c.id}`} className="w-full">
          Mark as completed (+{fmtCpd(c.cpd_credits)} CPD)
        </Button>
      )}
    </div>
  );
}

export function CourseCard({ c, actions, compact = false, index = 0 }) {
  const isLinkedIn = c.source !== "sustek";
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index, 8) * 0.04 }}
      data-testid={`course-${c.id}`}
      className={cn("bg-white rounded-2xl border shadow-sm p-5 flex flex-col", c.pinned ? "border-emerald-300" : "border-slate-200/80", compact && "min-w-[280px] max-w-[300px] snap-start")}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <SourceBadge source={c.source} />
        <div className="flex items-center gap-1.5">
          {c.pinned && <Pin className="w-3.5 h-3.5 text-emerald-600" data-testid="course-pinned" />}
          {c.link_type !== "search" && (
            <span className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-600" data-testid="course-cpd">+{fmtCpd(c.cpd_credits)} CPD</span>
          )}
        </div>
      </div>
      <div className="flex items-start gap-2.5">
        <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0", isLinkedIn ? "bg-sky-50 text-sky-600" : "bg-emerald-50 text-emerald-600")}><GraduationCap className="w-4.5 h-4.5 w-[18px] h-[18px]" /></div>
        <div className="min-w-0">
          <div className="font-display font-semibold text-slate-900 leading-snug line-clamp-2" data-testid="course-title">{c.title}</div>
          {!compact && c.description && <div className="text-sm text-slate-500 mt-1 line-clamp-2">{c.description}</div>}
        </div>
      </div>
      {c.rationale && (
        <div className="mt-3 text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-2.5 py-2 flex gap-1.5 leading-snug" data-testid="course-rationale">
          <Sparkles className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" /> <span className="line-clamp-2">{c.rationale}</span>
        </div>
      )}
      <div className="flex items-center gap-2 mt-3 text-xs text-slate-400 flex-wrap flex-1 content-start">
        {c.duration && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {c.duration_estimated ? "~" : ""}{c.duration}</span>}
        <span className={cn("px-1.5 py-0.5 rounded-md border text-[11px]", LEVEL_CLS[c.proficiency] || LEVEL_CLS.working)}>{c.level_label}</span>
        {(c.skills || []).slice(0, 2).map((s) => <span key={s} className="px-1.5 py-0.5 rounded-md bg-slate-50 text-slate-500 text-[11px]">{s}</span>)}
      </div>
      {c.status !== "not_started" && (
        <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${c.progress}%` }} /></div>
      )}
      <div className="mt-4">{isLinkedIn ? <LinkedInActions c={c} actions={actions} /> : <SustekActions c={c} actions={actions} />}</div>
    </motion.div>
  );
}
